import { lazy, Suspense, useEffect, useState } from "react";
import { useParams, useNavigate, useLocation } from "react-router-dom";
import { useAuth } from "@/hooks/useAuth";
import { useTranslation } from "react-i18next";
import { getCollectionBySlug, updateCollection, deleteCollection } from "@/services/collections";
import { getMessagesByCollection, subscribeToMessages, unsubscribeFromMessages } from "@/services/messages";
import { getContributionsByCollection } from "@/services/contributions";
import { getRsvpsByCollection } from "@/services/rsvps";
import { getTemplateImageUrl } from "@/services/templates";
import { Button } from "@/components/ui/button";
import { Progress } from "@/components/ui/progress";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import { Textarea } from "@/components/ui/textarea";
import { Check, X, Users, Pencil, Settings, Search, Heart, PackageCheck, Palette, ExternalLink, ThumbsDown, CalendarDays, FileText, Gift, CreditCard, Trash2, MessageSquare, Sparkles, Loader2, ShoppingBag } from "lucide-react";
import { ChevronDown } from "lucide-react";
import { format } from "date-fns";
import { Calendar } from "@/components/ui/calendar";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import { cn } from "@/lib/utils";
import { motion, AnimatePresence } from "framer-motion";
import { useToast } from "@/hooks/use-toast";
import ContributeWizard from "@/components/card/ContributeWizard";
import InviteModal from "@/components/card/InviteModal";
import SettingsModal from "@/components/card/SettingsModal";
import GiftGuidanceWizard from "@/components/card/GiftGuidanceWizard";
import SignersBubbles, { SignerDetail, BubbleMedia, colorForName, getInitials } from "@/components/card/SignersBubbles";
import CardEnvelope from "@/components/card/CardEnvelope";
import ProductPoolHero from "@/components/card/ProductPoolHero";
import AppHeader from "@/components/AppHeader";
import { Collapsible, CollapsibleContent, CollapsibleTrigger } from "@/components/ui/collapsible";
import { useCurrency } from "@/hooks/useCurrency";
import { supabase } from "@/integrations/supabase/client";

import type { Tables } from "@/integrations/supabase/types";

const FabricEditor = lazy(() => import("@/components/admin/FabricEditor"));

const CardManage = () => {
  const { slug } = useParams();
  const navigate = useNavigate();
  const location = useLocation();
  const fromDashboard = !!(location.state as { fromDashboard?: boolean })?.fromDashboard;
  const { toast } = useToast();
  const { user, loading: authLoading } = useAuth();
  const { t } = useTranslation();
  const currency = useCurrency();

  const [collection, setCollection] = useState<Tables<"collections"> | null>(null);
  const [messages, setMessages] = useState<Tables<"messages">[]>([]);
  const [contributions, setContributions] = useState<Tables<"contributions">[]>([]);
  const [templateUrl, setTemplateUrl] = useState("");
  const [loading, setLoading] = useState(true);
  const [fetchError, setFetchError] = useState(false);
  const [showGiftWizard, setShowGiftWizard] = useState(false);
  const [showDeleteDialog, setShowDeleteDialog] = useState(false);
  const [deleting, setDeleting] = useState(false);
  const [showMessageModal, setShowMessageModal] = useState(false);
  const [messageDraft, setMessageDraft] = useState("");
  const [savingMessage, setSavingMessage] = useState(false);
  const [wizardMode, setWizardMode] = useState<"sign" | "chipIn" | null>(null);
  const [showDateModal, setShowDateModal] = useState(false);
  const [dateOption, setDateOption] = useState<"update" | "close">("update");
  const [newDeadline, setNewDeadline] = useState<Date | undefined>(undefined);
  const [savingDate, setSavingDate] = useState(false);
  const [showGroupModal, setShowGroupModal] = useState(false);
  const [groupSearch, setGroupSearch] = useState("");
  const [showInviteModal, setShowInviteModal] = useState(false);
  const [settingsSection, setSettingsSection] = useState<"home" | "details" | "design" | "gifts" | "chipin" | "rsvp" | "danger" | null>(null);
  const [rsvps, setRsvps] = useState<any[]>([]);
  const [selectedGroupMemberId, setSelectedGroupMemberId] = useState<string | null>(null);
  const [cardPayment, setCardPayment] = useState<{ status: string; total_amount: number; currency: string } | null>(null);
  const [templatePrice, setTemplatePrice] = useState<number | null>(null);
  const [publishing, setPublishing] = useState(false);
  const [initiatingPayment, setInitiatingPayment] = useState(false);
  const [showProductModal, setShowProductModal] = useState(false);
  // ── Customizer ──
  const [customizerOpen, setCustomizerOpen] = useState(false);
  const [customizerJson, setCustomizerJson] = useState<Record<string, unknown> | null>(null);
  const [loadingTemplateJson, setLoadingTemplateJson] = useState(false);

  const handleOpenCustomizer = async () => {
    if (!collection?.card_template_id) {
      toast({
        title: t("manage.notCustomizable", { defaultValue: "Not customizable" }),
        description: t("manage.notCustomizableNoTemplate", {
          defaultValue: "This card doesn't have a template attached.",
        }),
      });
      return;
    }
    setLoadingTemplateJson(true);
    try {
      // Prefer any prior customization stored on the collection so the user
      // continues editing their own copy. Otherwise, start fresh from the
      // template's master JSON. Either way the user's edits are saved back to
      // `collections.card_design_json` and never touch the template itself.
      const existingDesign = (collection as unknown as { card_design_json?: Record<string, unknown> | null }).card_design_json;
      if (existingDesign) {
        setCustomizerJson(existingDesign);
      } else {
        const { data: tmpl, error } = await supabase
          .from("card_templates")
          .select("polotno_json")
          .eq("id", collection.card_template_id)
          .maybeSingle();
        if (error) throw error;
        if (!tmpl?.polotno_json) {
          toast({
            title: t("manage.notCustomizable", { defaultValue: "Not customizable" }),
            description: t("manage.notCustomizableDesc", {
              defaultValue: "This template does not have an editable design yet.",
            }),
          });
          return;
        }
        setCustomizerJson(tmpl.polotno_json as Record<string, unknown>);
      }
      setCustomizerOpen(true);
    } catch (err: unknown) {
      const message = err instanceof Error ? err.message : "Failed to load template";
      toast({ variant: "destructive", title: "Error", description: message });
    } finally {
      setLoadingTemplateJson(false);
    }
  };

  const handleCustomizerSave = async ({
    coverUrl,
    designJson,
  }: {
    coverUrl: string;
    designJson: Record<string, unknown>;
  }) => {
    if (!collection) return;
    try {
      const { error } = await updateCollection(collection.id, {
        custom_cover_url: coverUrl,
        card_design_json: designJson as unknown as null,
      });
      if (error) throw error;
      setCollection({
        ...collection,
        custom_cover_url: coverUrl,
        card_design_json: designJson as unknown as null,
      } as typeof collection);
      setTemplateUrl(coverUrl);
      setCustomizerOpen(false);
      toast({ title: t("manage.cardUpdated", { defaultValue: "Card updated" }) });
    } catch (err: unknown) {
      const message = err instanceof Error ? err.message : "Failed to save";
      toast({ variant: "destructive", title: "Error", description: message });
    }
  };

  useEffect(() => {
    let channel: ReturnType<typeof subscribeToMessages> | null = null;

    const fetchCard = async () => {
      try {
        const { data: col, error: colError } = await getCollectionBySlug(slug!);
        if (colError) { setFetchError(true); setLoading(false); return; }
        if (!col) { setLoading(false); return; }
        setCollection(col);

        const [templateResult, messagesResult, contributionsResult, rsvpsResult, paymentResult, tmplPriceResult] = await Promise.all([
          col.card_template_id ? getTemplateImageUrl(col.card_template_id) : null,
          getMessagesByCollection(col.id),
          getContributionsByCollection(col.id),
          getRsvpsByCollection(col.id),
          supabase
            .from("card_payments" as any)
            .select("status, total_amount, currency")
            .eq("collection_id", col.id)
            .order("created_at", { ascending: false })
            .limit(1)
            .maybeSingle(),
          col.card_template_id
            ? supabase.from("card_templates").select("price").eq("id", col.card_template_id).maybeSingle()
            : null,
        ]);

        setTemplateUrl(col.custom_cover_url || templateResult?.data?.image_url || "");
        setMessages(messagesResult.data);
        if (contributionsResult.data) setContributions(contributionsResult.data);
        setRsvps(rsvpsResult.data || []);
        if (paymentResult.data) setCardPayment(paymentResult.data as any);
        setTemplatePrice(tmplPriceResult?.data?.price ?? null);

        setLoading(false);

        channel = subscribeToMessages(col.id, (newMessage) => {
          setMessages((prev) => [...prev, newMessage as Tables<"messages">]);
        });
      } catch {
        setFetchError(true);
        setLoading(false);
      }
    };
    fetchCard();

    return () => { if (channel) unsubscribeFromMessages(channel); };
  }, [slug]);

  // Re-derive templateUrl when collection design changes
  useEffect(() => {
    if (!collection) return;
    const resolve = async () => {
      if (collection.custom_cover_url) {
        setTemplateUrl(collection.custom_cover_url);
      } else if (collection.card_template_id) {
        const result = await getTemplateImageUrl(collection.card_template_id);
        setTemplateUrl(result?.data?.image_url || "");
      } else {
        setTemplateUrl("");
      }
    };
    resolve();
  }, [collection?.custom_cover_url, collection?.card_template_id]);

  // Re-fetch template price when template changes (e.g. switched to free)
  useEffect(() => {
    if (!collection?.card_template_id) { setTemplatePrice(null); return; }
    const fetchPrice = async () => {
      const { data } = await supabase
        .from("card_templates")
        .select("price")
        .eq("id", collection.card_template_id!)
        .maybeSingle();
      setTemplatePrice(data?.price ?? null);
    };
    fetchPrice();
  }, [collection?.card_template_id]);

  // Redirect non-organizers
  useEffect(() => {
    if (!authLoading && !loading && collection) {
      if (!user || user.id !== collection.organizer_id) {
        navigate(`/card/${slug}`, { replace: true });
      }
    }
  }, [user, authLoading, loading, collection, slug, navigate]);

  // Handle return from Stripe after template upgrade payment
  useEffect(() => {
    if (!collection) return;
    const params = new URLSearchParams(location.search);
    const upgradeTemplateId = params.get("upgrade_template");
    if (!upgradeTemplateId) return;

    const applyUpgrade = async () => {
      const { error } = await updateCollection(collection.id, {
        card_template_id: upgradeTemplateId,
        custom_cover_url: null,
      });
      if (error) {
        toast({ variant: "destructive", title: t("common.error"), description: error.message });
      } else {
        setCollection({ ...collection, card_template_id: upgradeTemplateId, custom_cover_url: null });
        toast({ title: t("manage.designUpgraded", { defaultValue: "Design upgraded!" }), description: t("manage.designUpgradedDesc", { defaultValue: "Your card now uses the new design." }) });
      }
      // Clean URL params
      navigate(`/card/${slug}/manage`, { replace: true });
    };
    applyUpgrade();
  }, [collection?.id]);

  const openMessageModal = () => {
    setMessageDraft(collection?.message_to_group || "");
    setShowMessageModal(true);
  };

  const handleSaveMessage = async () => {
    if (!collection) return;
    setSavingMessage(true);
    const trimmed = messageDraft.trim();
    const { error } = await updateCollection(collection.id, {
      message_to_group: trimmed || null,
    });
    if (error) {
      toast({ variant: "destructive", title: t("common.error"), description: error.message });
      setSavingMessage(false);
      return;
    }
    setCollection({ ...collection, message_to_group: trimmed || null });
    toast({ title: t("settings.messageSaved", { defaultValue: "Message saved" }) });
    setSavingMessage(false);
    setShowMessageModal(false);
  };

  const handleDelete = async () => {
    if (!collection) return;
    setDeleting(true);
    const { error } = await deleteCollection(collection.id);
    if (error) {
      toast({ variant: "destructive", title: t("common.error"), description: error.message });
      setDeleting(false);
      return;
    }
    toast({ title: t("settings.deleted"), description: t("settings.deletedDesc") });
    setShowDeleteDialog(false);
    setDeleting(false);
    navigate("/dashboard");
  };

  const handleSaveDate = async () => {
    if (!collection) return;
    setSavingDate(true);
    if (dateOption === "close") {
      const { error } = await updateCollection(collection.id, {
        status: "closed",
        deadline: new Date().toISOString(),
      });
      if (error) {
        toast({ variant: "destructive", title: t("common.error"), description: error.message });
      } else {
        setCollection({ ...collection, status: "closed", deadline: new Date().toISOString() });
        toast({ title: t("cardView.cardClosed"), description: t("cardView.cardClosedDesc") });
      }
    } else if (newDeadline) {
      const { error } = await updateCollection(collection.id, {
        deadline: newDeadline.toISOString(),
      });
      if (error) {
        toast({ variant: "destructive", title: t("common.error"), description: error.message });
      } else {
        setCollection({ ...collection, deadline: newDeadline.toISOString() });
        toast({ title: t("cardView.dateUpdated"), description: t("cardView.dateUpdatedDesc") });
      }
    }
    setSavingDate(false);
    setShowDateModal(false);
  };

  const isDraft = collection?.status === "draft";
  const isPaidTemplate = templatePrice != null && templatePrice > 0;
  const paymentPending = isDraft && isPaidTemplate && (!cardPayment || cardPayment.status !== "completed");

  const handlePublish = async () => {
    if (!collection) return;
    setPublishing(true);
    const { error } = await updateCollection(collection.id, { status: "active" });
    if (error) {
      toast({ variant: "destructive", title: t("common.error"), description: error.message });
    } else {
      setCollection({ ...collection, status: "active" });
      toast({ title: t("manage.published", { defaultValue: "Card published!" }), description: t("manage.publishedDesc", { defaultValue: "Your card is now live and can be shared with guests." }) });
    }
    setPublishing(false);
  };

  const handleCompletePayment = async () => {
    if (!collection) return;
    setInitiatingPayment(true);
    try {
      const { data: paymentData, error: paymentError } = await supabase.functions.invoke(
        "create-card-payment",
        { body: { collection_id: collection.id, template_id: collection.card_template_id } }
      );
      if (paymentError || !paymentData?.url) {
        toast({ variant: "destructive", title: t("common.error"), description: t("manage.paymentFailed", { defaultValue: "Failed to initiate payment. Please try again." }) });
        setInitiatingPayment(false);
        return;
      }
      window.location.href = paymentData.url;
    } catch {
      toast({ variant: "destructive", title: t("common.error"), description: t("manage.paymentFailed", { defaultValue: "Failed to initiate payment. Please try again." }) });
      setInitiatingPayment(false);
    }
  };

  const deadlineFormatted = collection?.deadline
    ? format(new Date(collection.deadline), "d MMMM yyyy")
    : null;

  if (loading || authLoading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-background">
        <div className="animate-pulse text-muted-foreground">{t("cardView.loadingCard")}</div>
      </div>
    );
  }
  if (fetchError) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-background">
        <div className="text-center max-w-md px-6">
          <h1 className="text-2xl font-bold text-foreground mb-2">{t("cardView.somethingWrong")}</h1>
          <p className="text-muted-foreground mb-4">{t("cardView.couldntLoad")}</p>
          <button
            onClick={() => window.location.reload()}
            className="px-4 py-2 bg-primary text-primary-foreground rounded-full text-sm font-medium hover:bg-primary/90 transition-colors"
          >
            {t("cardView.retry")}
          </button>
        </div>
      </div>
    );
  }
  if (!collection) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-background">
        <div className="text-center">
          <h1 className="text-2xl font-bold text-foreground mb-2">{t("cardView.notFound")}</h1>
          <p className="text-muted-foreground">{t("cardView.notFoundDesc")}</p>
        </div>
      </div>
    );
  }

  const isOrganizer = user && user.id === collection.organizer_id;
  if (!isOrganizer) return null;

  const isProductPool = (collection as { pool_type?: string }).pool_type === "product";
  const productCol = collection as unknown as {
    product_name?: string | null;
    product_image?: string | null;
    product_store?: string | null;
    product_url?: string | null;
    product_price?: number | null;
    shipping_name?: string | null;
    shipping_address?: string | null;
    shipping_city?: string | null;
    shipping_phone?: string | null;
  };

  return (
    <div className="min-h-screen bg-muted/30">
      <AppHeader userMenuVariant="dashboard" onBack={() => {
        if (fromDashboard) {
          navigate("/dashboard");
        } else {
          navigate(`/card/${slug}`);
        }
      }} />

      {/* Header banner */}
      <div className="bg-primary relative overflow-hidden">
        <div className="absolute inset-0 opacity-[0.07]">
          <div className="absolute top-0 left-0 w-64 h-64 rounded-full bg-primary-foreground -translate-x-1/2 -translate-y-1/2" />
          <div className="absolute bottom-0 right-1/4 w-48 h-48 rounded-full bg-primary-foreground translate-y-1/2" />
          <div className="absolute top-1/2 right-0 w-32 h-32 rounded-full bg-primary-foreground translate-x-1/2 -translate-y-1/2" />
        </div>
        <div className="max-w-6xl mx-auto px-6 py-10 md:py-14 flex items-center justify-between gap-4 relative z-10">
          <div className="space-y-2 flex-1 min-w-0">
            <span className="text-[10px] font-light uppercase tracking-[0.3em] text-primary-foreground/60">
              {collection.occasion?.replace(/_/g, " ") || t("cardView.celebration")}
            </span>
            <div className="flex items-center gap-3">
              <h1 className="text-3xl md:text-4xl lg:text-5xl font-extralight text-primary-foreground tracking-tight leading-tight">
                {collection.title}
              </h1>
            </div>
            <div className="flex flex-wrap items-center gap-x-3 gap-y-1 pt-1">
              <span className="text-xs font-light text-primary-foreground/50">
                {t("cardView.for")} {collection.recipient_name}
              </span>
              {collection.deadline && (
                <>
                  <span className="w-1 h-1 rounded-full bg-primary-foreground/30" />
                  <span className="text-xs font-light text-primary-foreground/50">
                    {t("cardView.due")} {format(new Date(collection.deadline), "MMM d, yyyy")}
                  </span>
                </>
              )}
              <span className="w-1 h-1 rounded-full bg-primary-foreground/30" />
              <span className="text-xs font-light text-primary-foreground/50">
                {messages.length} {messages.length === 1 ? t("cardView.signer") : t("cardView.signers")}
              </span>
            </div>
            <div className="w-full overflow-x-auto overscroll-x-contain scrollbar-hide pt-3 snap-x [-webkit-overflow-scrolling:touch]">
              <div className="flex items-start gap-3 w-max [&>*]:snap-start">
                {[
                  {
                    key: "invite",
                    icon: Users,
                    label: "Invite",
                    onClick: () => (paymentPending ? handleCompletePayment() : setShowInviteModal(true)),
                    disabled: initiatingPayment,
                  },
                  {
                    key: "view",
                    icon: ExternalLink,
                    label: "View",
                    onClick: () => navigate(`/card/${collection.share_slug}`, { state: { fromDashboard: true } }),
                  },
                  {
                    key: "details",
                    icon: FileText,
                    label: t("settings.details", { defaultValue: "Details" }),
                    onClick: () => setSettingsSection("details"),
                  },
                  {
                    key: "design",
                    icon: Palette,
                    label: isProductPool
                      ? t("settings.card", { defaultValue: "Card" })
                      : t("settings.design", { defaultValue: "Design" }),
                    onClick: () => setSettingsSection("design"),
                  },
                  !isProductPool && {
                    key: "message",
                    icon: MessageSquare,
                    label: t("settings.messageToGroup", { defaultValue: "Message" }),
                    onClick: openMessageModal,
                  },
                  isProductPool
                    ? {
                        key: "product",
                        icon: ShoppingBag,
                        label: t("settings.product", { defaultValue: "Product" }),
                        onClick: () => setShowProductModal(true),
                      }
                    : {
                        key: "gifts",
                        icon: Gift,
                        label: t("settings.giftGuidance", { defaultValue: "Gift Preferences" }),
                        onClick: () => setSettingsSection("gifts"),
                      },
                  {
                    key: "chipin",
                    icon: CreditCard,
                    label: t("settings.chipIn", { defaultValue: "Chip-in" }),
                    onClick: () => setSettingsSection("chipin"),
                  },
                  {
                    key: "rsvp",
                    icon: Users,
                    label: t("settings.rsvp", { defaultValue: "RSVP" }),
                    onClick: () => setSettingsSection("rsvp"),
                  },
                  {
                    key: "delete",
                    icon: Trash2,
                    label: t("settings.dangerZone", { defaultValue: "Delete" }),
                    onClick: () => setShowDeleteDialog(true),
                    destructive: true,
                  },
                ].filter(Boolean).map((item) => {
                  const { key, icon: Icon, label, onClick, disabled, destructive } = item as {
                    key: string;
                    icon: typeof Users;
                    label: string;
                    onClick: () => void;
                    disabled?: boolean;
                    destructive?: boolean;
                  };
                  return (
                  <button
                    key={key}
                    type="button"
                    onClick={onClick}
                    disabled={disabled}
                    className="group flex flex-col items-center gap-1 shrink-0 w-[72px] disabled:opacity-50 disabled:cursor-not-allowed"
                  >
                    <div
                      className={cn(
                        "w-[60px] h-[60px] rounded-full border flex items-center justify-center transition-all duration-200 group-hover:-translate-y-px group-hover:shadow-sm",
                        destructive
                          ? "bg-destructive/15 border-destructive/30 group-hover:bg-destructive/30 group-hover:border-destructive/60"
                          : "bg-primary-foreground/10 border-primary-foreground/20 group-hover:bg-primary-foreground/25 group-hover:border-primary-foreground/50",
                      )}
                    >
                      <Icon size={22} className="text-primary-foreground" />
                    </div>
                    <span className="text-[10px] text-primary-foreground/80 font-medium leading-tight max-w-[68px] truncate">
                      {label}
                    </span>
                  </button>
                  );
                })}
              </div>
            </div>
          </div>
          {(collection.collection_enabled || Number(collection.total_collected || 0) > 0) && (
            <div className="hidden sm:flex flex-col items-center justify-center w-32 h-32 md:w-36 md:h-36 rounded-full border-2 border-primary-foreground/20 backdrop-blur-sm">
              <span className="text-2xl md:text-3xl font-extralight text-primary-foreground tracking-tight">
                {currency} {Number(collection.total_collected || 0).toFixed(0)}
              </span>
              <span className="text-[9px] uppercase tracking-[0.2em] text-primary-foreground/50 font-light mt-1">
                {t("cardView.collected")}
              </span>
              {(collection as any).collection_goal && (collection as any).collection_goal_amount > 0 && (
                <span className="text-[9px] text-primary-foreground/40 mt-0.5">
                  {t("cardView.of")} {currency} {Number((collection as any).collection_goal_amount).toFixed(0)}
                </span>
              )}
            </div>
          )}
        </div>
      </div>

      {/* Draft / Payment banner */}
      {isDraft && (
        <div className="bg-amber-50 dark:bg-amber-500/10 border-b border-amber-200 dark:border-amber-500/20">
          <div className="max-w-5xl mx-auto px-4 sm:px-6 py-4">
            <div className="flex flex-col sm:flex-row items-start sm:items-center gap-3">
              <div className="flex items-center gap-2 flex-1 min-w-0">
                <span className="shrink-0 w-2 h-2 rounded-full bg-amber-500" />
                <div>
                  <p className="text-sm font-medium text-amber-900 dark:text-amber-200">
                    {paymentPending
                      ? t("manage.paymentRequired", { defaultValue: "Payment required" })
                      : t("manage.draftStatus", { defaultValue: "This card is a draft" })
                    }
                  </p>
                  <p className="text-xs text-amber-700 dark:text-amber-300/70">
                    {paymentPending
                      ? t("manage.paymentRequiredDesc", { defaultValue: "Complete payment to publish your card and start inviting guests." })
                      : t("manage.draftStatusDesc", { defaultValue: "Publish your card so guests can view and sign it." })
                    }
                  </p>
                </div>
              </div>
              {paymentPending ? (
                <Button
                  onClick={handleCompletePayment}
                  disabled={initiatingPayment}
                  size="sm"
                  className="rounded-full bg-amber-600 hover:bg-amber-700 text-white shrink-0"
                >
                  <CreditCard size={14} className="me-1.5" />
                  {initiatingPayment
                    ? t("manage.redirecting", { defaultValue: "Redirecting…" })
                    : t("manage.completePayment", { defaultValue: "Complete Payment" })
                  }
                </Button>
              ) : (
                <Button
                  onClick={handlePublish}
                  disabled={publishing}
                  size="sm"
                  className="rounded-full bg-gradient-primary-animated text-primary-foreground shadow-lg shadow-primary/25 shrink-0"
                >
                  {publishing
                    ? t("manage.publishing", { defaultValue: "Publishing…" })
                    : t("manage.publish", { defaultValue: "Publish Card" })
                  }
                </Button>
              )}
            </div>
          </div>
        </div>
      )}

      {/* Main content */}
      <div className="max-w-5xl mx-auto px-4 sm:px-6 py-6">
        <div className="space-y-6">
          {/* Top row: Card + Stats side by side on desktop, stacked on mobile */}
          <div className="flex flex-col md:flex-row md:items-start gap-6">
            {/* Card preview */}
            <div className="w-full md:w-[340px] lg:w-[400px] shrink-0 space-y-4 md:sticky md:top-6 md:h-fit">
              <div className="relative">
                {isProductPool ? (
                  <ProductPoolHero
                    collection={collection as unknown as Parameters<typeof ProductPoolHero>[0]["collection"]}
                    templateUrl={templateUrl}
                    t={t}
                  />
                ) : (
                  <>
                    <CardEnvelope
                      templateUrl={templateUrl}
                      title={collection.title}
                      occasion={collection.occasion}
                      tint={(collection as any).envelope_tint || undefined}
                    />

                    {/* Quick Customize button — only shown when the card has a
                        template the user can edit. */}
                    {collection.card_template_id && (
                      <button
                        onClick={handleOpenCustomizer}
                        disabled={loadingTemplateJson}
                        className="absolute top-3 end-3 z-10 flex items-center gap-1.5 rounded-full bg-background/95 hover:bg-background text-foreground border border-border shadow-md backdrop-blur-sm px-3 py-1.5 text-xs font-medium transition-all disabled:opacity-60"
                        title={t("manage.customizeCard", { defaultValue: "Customize card" })}
                      >
                        {loadingTemplateJson ? (
                          <Loader2 size={13} className="animate-spin" />
                        ) : (
                          <Sparkles size={13} />
                        )}
                        <span className="hidden sm:inline">
                          {t("manage.customize", { defaultValue: "Customize" })}
                        </span>
                      </button>
                    )}
                  </>
                )}
              </div>

            </div>

            {/* Right column cards */}
            <div className="flex-1 space-y-4">
              {/* Message to group + Gift guidance merged — hidden for
                  product pools since product pools don't collect gift
                  preferences (the product IS the gift). */}
              {!isProductPool && (
                <OrganizerGuidanceCard
                  collection={collection}
                  onAddGiftGuidance={() => setSettingsSection("gifts")}
                />
              )}


              {/* Card 1: Wishes & Chip-in */}
              <div className="bg-card border border-border rounded-2xl overflow-hidden">
                <div className="px-5 py-4 border-b border-border">
                  <h3 className="text-sm font-semibold text-foreground tracking-tight">{t("cardView.signsAndChipIn")}</h3>
                </div>
                <div className="px-5 py-3 flex flex-wrap gap-2">
                  {collection.deadline && (
                    <div className="inline-flex items-center gap-1.5 rounded-full bg-muted px-2.5 py-1">
                      <span className="w-1.5 h-1.5 rounded-full bg-green-500" />
                      <span className="text-[11px] text-foreground font-light">
                        {t("cardView.signingOpen")} {deadlineFormatted}
                      </span>
                      <button
                        onClick={() => {
                          setDateOption("update");
                          setNewDeadline(collection.deadline ? new Date(collection.deadline) : undefined);
                          setShowDateModal(true);
                        }}
                        className="text-[10px] text-muted-foreground hover:text-foreground ms-0.5 underline underline-offset-2"
                      >
                        {t("cardView.edit")}
                      </button>
                    </div>
                  )}
                  {collection.collection_enabled && (
                    <div className="inline-flex items-center gap-1.5 rounded-full bg-muted px-2.5 py-1">
                      <span className="w-1.5 h-1.5 rounded-full bg-green-500" />
                      <span className="text-[11px] text-foreground font-light">{t("cardView.chipInActive")}</span>
                    </div>
                  )}
                  {!collection.collection_enabled && Number(collection.total_collected || 0) > 0 && (
                    <div className="inline-flex items-center gap-1.5 rounded-full bg-muted px-2.5 py-1">
                      <span className="w-1.5 h-1.5 rounded-full bg-muted-foreground" />
                      <span className="text-[11px] text-muted-foreground font-light">{t("cardView.chipInClosed")}</span>
                    </div>
                  )}
                </div>

                {(collection as any).collection_note && (
                  <div className="px-5 pb-2">
                    <p className="text-xs text-muted-foreground leading-relaxed italic">
                      {(collection as any).collection_note}
                    </p>
                  </div>
                )}

                {/* Goal progress */}
                {(collection.collection_enabled || Number(collection.total_collected || 0) > 0) && (collection as any).collection_goal && (collection as any).collection_goal_amount > 0 && (
                  <div className="px-5 py-4 space-y-2.5">
                    <div className="flex items-end justify-between">
                      <div>
                        <p className="text-2xl font-extralight text-foreground tracking-tight leading-none">
                          {currency} {Number(collection.total_collected || 0).toFixed(0)}
                        </p>
                        <p className="text-[10px] text-muted-foreground uppercase tracking-[0.15em] mt-1">
                          {t("cardView.of")} {currency} {Number((collection as any).collection_goal_amount).toFixed(0)} {t("cardView.goal")}
                        </p>
                      </div>
                      <span className="text-lg font-extralight text-muted-foreground">
                        {Math.min(100, Math.round((Number(collection.total_collected || 0) / Number((collection as any).collection_goal_amount)) * 100))}%
                      </span>
                    </div>
                    <Progress
                      value={Math.min(100, (Number(collection.total_collected || 0) / Number((collection as any).collection_goal_amount)) * 100)}
                      className="h-2 [&>div]:bg-[hsl(340,60%,55%)]"
                      animate
                    />
                    {Number(collection.total_collected || 0) > 0 && (
                      <Button
                        variant="outline"
                        size="sm"
                        className="w-full rounded-full h-10 gap-2"
                        onClick={() => {
                          toast({
                            title: t("cardView.comingSoon"),
                            description: t("cardView.comingSoonDesc"),
                          });
                        }}
                      >
                        {t("cardView.redeemFunds")}
                      </Button>
                    )}
                  </div>
                )}
                {/* Collected without goal */}
                {(!((collection as any).collection_goal && (collection as any).collection_goal_amount > 0)) && (collection.collection_enabled || Number(collection.total_collected || 0) > 0) && (
                  <div className="px-5 py-4 space-y-2.5">
                    <div>
                      <p className="text-2xl font-extralight text-foreground tracking-tight leading-none">
                        {currency} {Number(collection.total_collected || 0).toFixed(0)}
                      </p>
                      <p className="text-[10px] text-muted-foreground uppercase tracking-[0.15em] mt-1">
                        {collection.collection_enabled ? t("cardView.totalCollected") : t("cardView.totalCollectedClosed")}
                      </p>
                    </div>
                    {Number(collection.total_collected || 0) > 0 && (
                      <Button
                        variant="outline"
                        size="sm"
                        className="w-full rounded-full h-10 gap-2"
                        onClick={() => {
                          toast({
                            title: t("cardView.comingSoon"),
                            description: t("cardView.comingSoonDesc"),
                          });
                        }}
                      >
                        {t("cardView.redeemFunds")}
                      </Button>
                    )}
                  </div>
                )}

              </div>

              {/* Card 2: Attendees */}
              {(collection as any).rsvp_enabled && rsvps.length > 0 && (
                <div className="bg-card border border-border rounded-2xl overflow-hidden">
                  <div className="px-5 py-4 border-b border-border flex items-center justify-between">
                    <h3 className="text-sm font-semibold text-foreground tracking-tight">{t("cardView.attendees", { defaultValue: "Attendees" })}</h3>
                    <span className="text-xs text-muted-foreground">
                      {rsvps.filter(r => r.response === "yes").length} {t("cardView.attending", { defaultValue: "attending" })}
                      {rsvps.filter(r => r.response === "no").length > 0 && ` · ${rsvps.filter(r => r.response === "no").length} ${t("cardView.declined", { defaultValue: "declined" })}`}
                    </span>
                  </div>
                  <div className="px-5 py-4">
                    <div className="flex items-center">
                      {rsvps.slice(0, 10).map((r: any, i: number) => {
                        const initials = (r.guest_name || "?")
                          .split(" ")
                          .map((w: string) => w[0])
                          .join("")
                          .toUpperCase()
                          .slice(0, 2);
                        const bgColors = ["bg-rose-100", "bg-amber-100", "bg-emerald-100", "bg-sky-100", "bg-violet-100", "bg-fuchsia-100", "bg-teal-100", "bg-orange-100"];
                        const textColors = ["text-rose-700", "text-amber-700", "text-emerald-700", "text-sky-700", "text-violet-700", "text-fuchsia-700", "text-teal-700", "text-orange-700"];
                        return (
                          <div
                            key={r.id || i}
                            className="relative"
                            style={{ zIndex: 10 - i, marginLeft: i === 0 ? 0 : -8 }}
                            title={r.guest_name}
                          >
                            <div
                              className={`w-9 h-9 rounded-full flex items-center justify-center text-[10px] font-medium ring-2 ring-background ${bgColors[i % bgColors.length]} ${textColors[i % textColors.length]}`}
                            >
                              {initials}
                            </div>
                            <div
                              className="absolute -bottom-0.5 -right-0.5 w-3.5 h-3.5 rounded-full flex items-center justify-center ring-2 ring-background"
                              style={{ backgroundColor: r.response === "yes" ? "#34a853" : r.response === "maybe" ? "#fbbc04" : "#9e9e9e" }}
                            >
                              {r.response === "yes" ? (
                                <Check size={7} className="text-white" strokeWidth={3} />
                              ) : (
                                <X size={7} className="text-white" strokeWidth={3} />
                              )}
                            </div>
                          </div>
                        );
                      })}
                      {rsvps.length > 10 && (
                        <div
                          className="w-9 h-9 rounded-full flex items-center justify-center text-[10px] font-medium bg-muted text-muted-foreground ring-2 ring-background"
                          style={{ zIndex: 0, marginLeft: -8 }}
                        >
                          +{rsvps.length - 10}
                        </div>
                      )}
                    </div>
                    <p className="text-xs text-muted-foreground font-light leading-relaxed mt-3">
                      {rsvps.slice(0, 5).map((r: any) => r.guest_name || "Guest").join(", ")}
                      {rsvps.length > 5 && ` +${rsvps.length - 5}`}
                    </p>
                  </div>
                  {/* Preference answers summary */}
                  {(() => {
                    const rsvpQuestions: { id: string; question: string; type: string; options: string[] }[] = Array.isArray((collection as any).rsvp_questions) ? (collection as any).rsvp_questions : [];
                    if (!rsvpQuestions.length) return null;
                    const yesRsvps = rsvps.filter((r: any) => r.response === "yes" && r.rsvp_answers && typeof r.rsvp_answers === "object");
                    if (!yesRsvps.length) return null;
                    return (
                      <Collapsible className="px-5 py-4 border-t border-border">
                        <CollapsibleTrigger className="flex items-center justify-between w-full group">
                          <p className="text-[10px] font-medium text-muted-foreground uppercase tracking-wider">{t("cardView.preferenceSummary", { defaultValue: "Preference Summary" })}</p>
                          <ChevronDown size={14} className="text-muted-foreground transition-transform group-data-[state=open]:rotate-180" />
                        </CollapsibleTrigger>
                        <CollapsibleContent className="space-y-3 pt-3">
                        {rsvpQuestions.map((q) => {
                          const tallies: Record<string, number> = {};
                          const freeTexts: string[] = [];
                          yesRsvps.forEach((r: any) => {
                            const ans = r.rsvp_answers?.[q.id];
                            if (!ans) return;
                            if (q.type === "text") {
                              if (typeof ans === "string" && ans.trim()) freeTexts.push(ans.trim());
                            } else if (Array.isArray(ans)) {
                              ans.forEach((a: string) => { tallies[a] = (tallies[a] || 0) + 1; });
                            } else if (typeof ans === "string") {
                              tallies[ans] = (tallies[ans] || 0) + 1;
                            }
                          });
                          const hasData = Object.keys(tallies).length > 0 || freeTexts.length > 0;
                          if (!hasData) return null;
                          return (
                            <div key={q.id}>
                              <p className="text-xs font-medium text-foreground mb-1.5">{q.question}</p>
                              {Object.keys(tallies).length > 0 && (
                                <div className="flex flex-wrap gap-1.5">
                                  {Object.entries(tallies).sort((a, b) => b[1] - a[1]).map(([option, count]) => (
                                    <span key={option} className="text-[10px] px-2 py-1 rounded-full bg-primary/10 text-primary font-medium">
                                      {option} · {count}
                                    </span>
                                  ))}
                                </div>
                              )}
                              {freeTexts.length > 0 && (
                                <ul className="text-xs text-muted-foreground space-y-0.5 mt-1">
                                  {freeTexts.map((txt, i) => <li key={i}>• {txt}</li>)}
                                </ul>
                              )}
                            </div>
                          );
                        })}
                        </CollapsibleContent>
                      </Collapsible>
                    );
                  })()}
                </div>
              )}

              {/* Card 3: Who Signed — always shown so organisers see a
                  clear empty state and can invite/share before anyone
                  has signed yet. */}
              <div className="bg-card border border-border rounded-2xl overflow-hidden">
                <div className="px-5 py-4 border-b border-border flex items-center justify-between">
                  <h3 className="text-sm font-semibold text-foreground tracking-tight">{t("cardView.whoSigned")}</h3>
                  {messages.length > 0 && (
                    <Button variant="ghost" size="sm" className="text-xs h-6 px-2 text-muted-foreground hover:text-foreground" onClick={() => setShowGroupModal(true)}>
                      {t("cardView.viewAll")}
                    </Button>
                  )}
                </div>
                <div className="px-4 py-4">
                  <SignersBubbles messages={messages} contributions={contributions} maxVisible={20} />
                </div>
                <div className="px-5 py-4 border-t border-border">
                  <Button
                    onClick={() => setWizardMode("sign")}
                    variant="outline"
                    className="w-full rounded-full h-10 gap-2"
                  >
                    <Pencil size={15} />
                    {t("cardView.signTheCard")}
                  </Button>
                </div>
              </div>

              {/* Enable RSVP + Enable Chip-in side by side. Product
                  pools hide the Enable RSVP tile since RSVP/event
                  attendance isn't part of the product-pool flow. */}
              {((!(collection as any).rsvp_enabled && !isProductPool) || !collection.collection_enabled) && (
                <div className={cn(
                  "grid gap-3",
                  !isProductPool && !(collection as any).rsvp_enabled && !collection.collection_enabled
                    ? "grid-cols-2"
                    : "grid-cols-1",
                )}>
                  {!(collection as any).rsvp_enabled && !isProductPool && (
                    <button
                      onClick={() => setSettingsSection("rsvp")}
                      className="bg-card border border-dashed border-border rounded-2xl p-4 flex flex-col items-center gap-2 hover:border-primary/40 hover:bg-primary/5 transition-colors group text-center"
                    >
                      <span className="text-xl">📍</span>
                      <div>
                        <p className="text-sm font-medium text-foreground group-hover:text-primary transition-colors">{t("cardView.enableRsvp")}</p>
                        <p className="text-[11px] text-muted-foreground leading-snug mt-0.5">{t("cardView.confirmAttendance")}</p>
                      </div>
                    </button>
                  )}
                  {!collection.collection_enabled && (
                    <button
                      onClick={async () => {
                        const updates = { collection_enabled: true, type: "card_collection" as const };
                        const { error } = await updateCollection(collection.id, updates);
                        if (error) {
                          toast({ variant: "destructive", title: t("common.error"), description: t("cardView.failedActivateChipIn") });
                        } else {
                          setCollection({ ...collection, ...updates });
                          toast({ title: t("cardView.chipInActivated"), description: t("cardView.chipInActivatedDesc") });
                          setSettingsSection("chipin");
                        }
                      }}
                      className="bg-card border border-dashed border-border rounded-2xl p-4 flex flex-col items-center gap-2 hover:border-primary/40 hover:bg-primary/5 transition-colors group text-center"
                    >
                      <span className="text-xl">💰</span>
                      <div>
                        <p className="text-sm font-medium text-foreground group-hover:text-primary transition-colors">{t("cardView.enableChipIn")}</p>
                        <p className="text-[11px] text-muted-foreground leading-snug mt-0.5">{t("cardView.collectMoneyTogether")}</p>
                      </div>
                    </button>
                  )}
                </div>
              )}

              {/* RSVP Settings shortcut - when enabled */}
              {(collection as any).rsvp_enabled && (
                <div className="flex justify-end">
                  <Button variant="ghost" size="sm" className="text-xs h-7 gap-1.5 text-muted-foreground hover:text-foreground" onClick={() => setSettingsSection("rsvp")}>
                    <Settings size={13} />
                    {t("cardView.editRsvpSettings", { defaultValue: "Edit RSVP Settings" })}
                  </Button>
                </div>
              )}
            </div>
          </div>
        </div>
      </div>

      {/* Contribute Wizard */}
      <AnimatePresence>
        {wizardMode && (
          <ContributeWizard
            collection={collection}
            templateUrl={templateUrl}
            messages={messages}
            chipIn={wizardMode === "chipIn"}
            onClose={() => setWizardMode(null)}
            onSigned={() => setWizardMode(null)}
          />
        )}
      </AnimatePresence>

      {/* Change date modal */}
      <AnimatePresence>
        {showDateModal && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            className="fixed inset-0 z-50 bg-black/50 flex items-end sm:items-center justify-center sm:p-4"
            onClick={() => setShowDateModal(false)}
          >
            <motion.div
              initial={{ opacity: 0, y: 100 }}
              animate={{ opacity: 1, y: 0 }}
              exit={{ opacity: 0, y: 100 }}
              transition={{ type: "spring", damping: 25, stiffness: 300 }}
              onClick={(e) => e.stopPropagation()}
              className="bg-background rounded-t-2xl sm:rounded-2xl w-full sm:max-w-sm shadow-2xl relative max-h-[90vh] overflow-y-auto"
            >
              <div className="flex items-center justify-between px-6 py-4 border-b border-border sticky top-0 bg-background rounded-t-2xl z-10">
                <h2 className="text-base font-bold text-foreground font-serif">{t("cardView.changeCloseDate")}</h2>
                <button
                  onClick={() => setShowDateModal(false)}
                  className="w-7 h-7 rounded-full bg-muted flex items-center justify-center text-muted-foreground hover:text-foreground"
                >
                  <X size={14} />
                </button>
              </div>
              <div className="p-5 space-y-4">
                <button
                  onClick={() => setDateOption("update")}
                  className={cn(
                    "w-full text-start rounded-xl border p-4 transition-all",
                    dateOption === "update"
                      ? "border-primary bg-primary/5 ring-1 ring-primary/20"
                      : "border-border hover:border-primary/30"
                  )}
                >
                  <div className="flex items-center gap-3">
                    <div className={cn(
                      "w-5 h-5 rounded-full border-2 flex items-center justify-center shrink-0",
                      dateOption === "update" ? "border-primary" : "border-muted-foreground/30"
                    )}>
                      {dateOption === "update" && <div className="w-2.5 h-2.5 rounded-full bg-primary" />}
                    </div>
                    <div>
                      <span className="text-sm font-medium text-foreground">{t("cardView.updateCloseDate")}</span>
                      <p className="text-[11px] text-muted-foreground mt-0.5">{t("cardView.pickNewDeadline")}</p>
                    </div>
                  </div>
                  {dateOption === "update" && (
                    <div className="mt-3 ms-8">
                      <Popover>
                        <PopoverTrigger asChild>
                          <button className="w-full text-start text-sm bg-muted/80 border border-border rounded-lg px-4 py-2.5 text-foreground hover:bg-muted transition-colors flex items-center justify-between">
                            <span>{newDeadline ? format(newDeadline, "dd MMM yyyy") : t("cardView.pickDate")}</span>
                            <CalendarDays size={14} className="text-muted-foreground" />
                          </button>
                        </PopoverTrigger>
                        <PopoverContent className="w-auto p-0" align="start">
                          <Calendar
                            mode="single"
                            selected={newDeadline}
                            onSelect={setNewDeadline}
                            disabled={(date) => date < new Date()}
                            initialFocus
                            className={cn("p-3 pointer-events-auto")}
                          />
                        </PopoverContent>
                      </Popover>
                    </div>
                  )}
                </button>

                <button
                  onClick={() => setDateOption("close")}
                  className={cn(
                    "w-full text-start rounded-xl border p-4 transition-all",
                    dateOption === "close"
                      ? "border-destructive/50 bg-destructive/5 ring-1 ring-destructive/20"
                      : "border-border hover:border-destructive/30"
                  )}
                >
                  <div className="flex items-center gap-3">
                    <div className={cn(
                      "w-5 h-5 rounded-full border-2 flex items-center justify-center shrink-0",
                      dateOption === "close" ? "border-destructive" : "border-muted-foreground/30"
                    )}>
                      {dateOption === "close" && <div className="w-2.5 h-2.5 rounded-full bg-destructive" />}
                    </div>
                    <div>
                      <span className="text-sm font-medium text-foreground">{t("cardView.closeNow")}</span>
                      <p className="text-[11px] text-muted-foreground mt-0.5">{t("cardView.closeNowDesc")}</p>
                    </div>
                  </div>
                </button>

                <Button
                  onClick={handleSaveDate}
                  disabled={savingDate || (dateOption === "update" && !newDeadline)}
                  className="w-full rounded-full bg-primary text-primary-foreground hover:bg-primary/90"
                  size="lg"
                >
                  {savingDate ? t("cardView.saving") : t("common.save")}
                </Button>
              </div>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>

      {/* Your Group modal */}
      <AnimatePresence>
        {showGroupModal && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            className="fixed inset-0 z-50 bg-black/50 flex items-end sm:items-center justify-center sm:p-4"
            onClick={() => setShowGroupModal(false)}
          >
            <motion.div
              initial={{ opacity: 0, y: 100 }}
              animate={{ opacity: 1, y: 0 }}
              exit={{ opacity: 0, y: 100 }}
              transition={{ type: "spring", damping: 25, stiffness: 300 }}
              onClick={(e) => e.stopPropagation()}
              className="bg-background rounded-t-2xl sm:rounded-2xl w-full sm:max-w-2xl max-h-[85vh] shadow-2xl flex flex-col relative overflow-hidden"
            >
              <div className="flex items-center justify-center py-5 relative shrink-0 border-b border-border">
                <h2 className="text-lg font-bold text-foreground font-serif">{t("cardView.yourGroup")}</h2>
                <button
                  onClick={() => setShowGroupModal(false)}
                  className="w-8 h-8 rounded-full bg-muted flex items-center justify-center text-muted-foreground hover:text-foreground absolute top-4 end-4"
                >
                  <X size={16} />
                </button>
              </div>

              <div className="flex-1 overflow-y-auto">
                <div className="flex items-center justify-around px-5 py-4">
                  <div className="text-center">
                    <p className="text-xl font-bold text-foreground">{new Set(messages.map(m => m.contributor_email || m.contributor_name)).size}</p>
                    <p className="text-[11px] text-muted-foreground mt-0.5">{t("cardView.signed")}</p>
                  </div>
                  <div className="text-center">
                    <p className="text-xl font-bold text-foreground">{contributions.filter(c => c.status === "completed").length}</p>
                    <p className="text-[11px] text-muted-foreground mt-0.5">{t("cardView.contributed")}</p>
                  </div>
                  <div className="text-center">
                    <p className="text-xl font-bold text-foreground">
                      {currency} {contributions.filter(c => c.status === "completed").reduce((sum, c) => sum + Number(c.amount), 0).toFixed(0)}
                    </p>
                    <p className="text-[11px] text-muted-foreground mt-0.5">{t("cardView.total")}</p>
                  </div>
                </div>

                <div className="px-5 pb-3 space-y-3">
                  <div className="flex items-center gap-2">
                    <div className="flex items-center gap-1.5 flex-1 min-w-0 border border-border rounded-lg px-2.5 py-1.5 bg-muted/30">
                      <Search size={14} className="text-muted-foreground shrink-0" />
                      <input
                        value={groupSearch}
                        onChange={(e) => setGroupSearch(e.target.value)}
                        placeholder={t("cardView.searchByName")}
                        className="bg-transparent outline-none text-sm text-foreground placeholder:text-muted-foreground w-full"
                      />
                    </div>
                  </div>
                  <div className="flex items-center gap-2">
                    <Button size="sm" className="rounded-full text-xs" onClick={() => {
                      toast({ title: t("cardView.comingSoon"), description: t("cardView.groupMessagingSoon") });
                    }}>
                      {t("cardView.sendGroupMessage")}
                    </Button>
                    <Button size="sm" variant="outline" className="rounded-full text-xs" onClick={() => { setShowGroupModal(false); setShowInviteModal(true); }}>
                      {t("cardView.invitePeople")}
                    </Button>
                  </div>
                </div>

                <div className="px-5 pb-5">
                  {(() => {
                    const contributionMap = new Map<string, Tables<"contributions">>();
                    contributions.forEach(c => {
                      const existing = contributionMap.get(c.contributor_name);
                      if (!existing || c.status === "completed") {
                        contributionMap.set(c.contributor_name, c);
                      }
                    });

                    type MemberItem = {
                      id: string;
                      name: string;
                      email: string | null;
                      signed: boolean;
                      signedDate: string | null;
                      contributed: boolean;
                      amount: number;
                      contributedDate: string | null;
                      messagePreview: string | null;
                    };

                    const membersMap = new Map<string, MemberItem>();

                    messages.forEach(m => {
                      const contrib = contributionMap.get(m.contributor_name);
                      membersMap.set(m.contributor_name, {
                        id: m.id,
                        name: m.contributor_name,
                        email: m.contributor_email,
                        signed: true,
                        signedDate: m.created_at,
                        contributed: contrib?.status === "completed",
                        amount: contrib?.status === "completed" ? Number(contrib.amount) : 0,
                        contributedDate: contrib?.created_at || null,
                        messagePreview: m.message_text?.slice(0, 50) || null,
                      });
                    });

                    contributions.forEach(c => {
                      if (!membersMap.has(c.contributor_name)) {
                        membersMap.set(c.contributor_name, {
                          id: c.id,
                          name: c.contributor_name,
                          email: c.contributor_email,
                          signed: false,
                          signedDate: null,
                          contributed: c.status === "completed",
                          amount: c.status === "completed" ? Number(c.amount) : 0,
                          contributedDate: c.created_at,
                          messagePreview: null,
                        });
                      }
                    });

                    let members = Array.from(membersMap.values());
                    if (groupSearch) {
                      const q = groupSearch.toLowerCase();
                      members = members.filter(m =>
                        m.name.toLowerCase().includes(q) ||
                        (m.email && m.email.toLowerCase().includes(q))
                      );
                    }

                    if (members.length === 0) {
                      return (
                        <div className="text-center py-12 text-muted-foreground text-sm">
                          {messages.length === 0 && contributions.length === 0
                            ? t("cardView.noMembersYet")
                            : t("cardView.noResults")}
                        </div>
                      );
                    }

                    const selectedMemberMessage = selectedGroupMemberId
                      ? messages.find(msg => msg.id === selectedGroupMemberId)
                      : null;
                    const selectedMemberContribution = selectedMemberMessage
                      ? contributions.find(c => c.contributor_email === selectedMemberMessage.contributor_email && c.status === "completed")
                      : undefined;

                    return (
                      <>
                        <div className="divide-y divide-border">
                          {members.map((m) => {
                            const message = messages.find(msg => msg.id === m.id);
                            return (
                              <button
                                key={m.id}
                                className="flex items-center gap-3 py-3 w-full text-start hover:bg-muted/50 transition-colors cursor-pointer"
                                onClick={() => {
                                  if (message) setSelectedGroupMemberId(message.id);
                                }}
                              >
                                <div className="relative shrink-0">
                                  {message ? (
                                    <BubbleMedia message={message} size="sm" />
                                  ) : (
                                    <div className={`w-14 h-14 rounded-full ring-2 ring-background flex items-center justify-center font-medium text-xs ${colorForName(m.name)}`}>
                                      {getInitials(m.name)}
                                    </div>
                                  )}
                                  {m.contributed && (
                                    <div className="absolute -bottom-0.5 -right-0.5 min-w-5 h-5 rounded-full bg-emerald-500 text-white flex items-center justify-center ring-2 ring-background px-1">
                                      <span className="text-[8px] font-bold leading-none">{currency} {m.amount.toFixed(0)}</span>
                                    </div>
                                  )}
                                </div>
                                <div className="min-w-0 flex-1">
                                  <p className="text-sm font-medium text-foreground truncate">{m.name}</p>
                                  {m.messagePreview && (
                                    <p className="text-xs text-muted-foreground/70 truncate italic mt-0.5">
                                      "{m.messagePreview}{m.messagePreview.length >= 50 ? "..." : ""}"
                                    </p>
                                  )}
                                </div>
                                <span className="text-[10px] text-muted-foreground shrink-0">
                                  {m.signedDate
                                    ? new Date(m.signedDate).toLocaleDateString()
                                    : m.contributedDate
                                      ? new Date(m.contributedDate).toLocaleDateString()
                                      : ""}
                                </span>
                              </button>
                            );
                          })}
                        </div>
                        <AnimatePresence>
                          {selectedMemberMessage && (
                            <SignerDetail
                              message={selectedMemberMessage}
                              contribution={selectedMemberContribution}
                              onClose={() => setSelectedGroupMemberId(null)}
                            />
                          )}
                        </AnimatePresence>
                      </>
                    );
                  })()}
                </div>
              </div>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>

      {/* Invite modal */}
      <AnimatePresence>
        {showInviteModal && collection && (
          <InviteModal
            collectionId={collection.id}
            collectionTitle={collection.title}
            shareSlug={collection.share_slug || undefined}
            organizerName={user?.user_metadata?.display_name}
            onClose={() => setShowInviteModal(false)}
          />
        )}
      </AnimatePresence>
      {/* Settings modal */}
      <AnimatePresence>
        {settingsSection !== null && collection && (
          <SettingsModal
            collection={collection}
            onClose={() => setSettingsSection(null)}
            onUpdate={(updates) => setCollection({ ...collection, ...updates })}
            initialSection={settingsSection}
          />
        )}
      </AnimatePresence>
      {/* Product details modal — product pools only */}
      <AnimatePresence>
        {showProductModal && isProductPool && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            className="fixed inset-0 z-50 bg-black/50 flex items-end sm:items-center justify-center sm:p-4"
            onClick={() => setShowProductModal(false)}
          >
            <motion.div
              initial={{ opacity: 0, y: 100 }}
              animate={{ opacity: 1, y: 0 }}
              exit={{ opacity: 0, y: 100 }}
              transition={{ type: "spring", damping: 25, stiffness: 300 }}
              onClick={(e) => e.stopPropagation()}
              className="bg-background rounded-t-2xl sm:rounded-2xl w-full sm:max-w-lg max-h-[90vh] overflow-y-auto shadow-2xl relative"
            >
              <div className="flex items-center py-4 border-b border-border sticky top-0 bg-background rounded-t-2xl z-10 px-4">
                <div className="w-8" />
                <h2 className="text-lg font-bold text-foreground font-serif flex-1 text-center">
                  {t("manage.productDetailsTitle", { defaultValue: "Product details" })}
                </h2>
                <button
                  onClick={() => setShowProductModal(false)}
                  className="w-8 h-8 rounded-full bg-muted flex items-center justify-center text-muted-foreground hover:text-foreground"
                >
                  <X size={16} />
                </button>
              </div>

              <div className="p-5 space-y-5">
                <div className="rounded-xl border border-border overflow-hidden bg-card">
                  <div className="aspect-[4/3] bg-gradient-to-b from-muted/40 to-muted/70 flex items-center justify-center p-6">
                    {productCol.product_image ? (
                      <img
                        src={productCol.product_image}
                        alt={productCol.product_name || ""}
                        className="max-h-full max-w-full object-contain"
                      />
                    ) : (
                      <ShoppingBag size={56} className="text-muted-foreground/50" />
                    )}
                  </div>
                  <div className="px-5 py-4 space-y-1.5 border-t border-border">
                    {productCol.product_store && (
                      <p className="text-[10px] font-medium uppercase tracking-[0.2em] text-muted-foreground">
                        {productCol.product_store}
                      </p>
                    )}
                    {productCol.product_name && (
                      <h3 className="text-base font-bold text-foreground leading-tight font-serif">
                        {productCol.product_name}
                      </h3>
                    )}
                    <div className="flex items-center justify-between pt-1.5">
                      <p className="text-lg font-bold text-foreground">
                        AED {Number(productCol.product_price || 0).toLocaleString()}
                      </p>
                      {productCol.product_url && (
                        <a
                          href={productCol.product_url}
                          target="_blank"
                          rel="noopener noreferrer"
                          className="text-xs font-medium text-primary hover:underline flex items-center gap-1"
                        >
                          {t("cardView.viewProduct", { defaultValue: "View product" })}
                          <ExternalLink size={12} />
                        </a>
                      )}
                    </div>
                  </div>
                </div>

                {(productCol.shipping_name || productCol.shipping_address || productCol.shipping_city || productCol.shipping_phone) && (
                  <div className="rounded-xl border border-border bg-card">
                    <div className="px-5 py-3 border-b border-border">
                      <p className="text-[10px] font-semibold text-muted-foreground uppercase tracking-[0.15em]">
                        {t("manage.shippingTo", { defaultValue: "Shipping to" })}
                      </p>
                    </div>
                    <dl className="px-5 py-3 text-sm text-foreground space-y-2">
                      {productCol.shipping_name && (
                        <div className="flex items-start justify-between gap-3">
                          <dt className="text-xs text-muted-foreground">{t("manage.recipient", { defaultValue: "Recipient" })}</dt>
                          <dd className="text-end">{productCol.shipping_name}</dd>
                        </div>
                      )}
                      {productCol.shipping_address && (
                        <div className="flex items-start justify-between gap-3">
                          <dt className="text-xs text-muted-foreground">{t("manage.address", { defaultValue: "Address" })}</dt>
                          <dd className="text-end">{productCol.shipping_address}</dd>
                        </div>
                      )}
                      {productCol.shipping_city && (
                        <div className="flex items-start justify-between gap-3">
                          <dt className="text-xs text-muted-foreground">{t("manage.city", { defaultValue: "City" })}</dt>
                          <dd className="text-end">{productCol.shipping_city}</dd>
                        </div>
                      )}
                      {productCol.shipping_phone && (
                        <div className="flex items-start justify-between gap-3">
                          <dt className="text-xs text-muted-foreground">{t("manage.phone", { defaultValue: "Phone" })}</dt>
                          <dd className="text-end">{productCol.shipping_phone}</dd>
                        </div>
                      )}
                    </dl>
                  </div>
                )}
              </div>

              <div className="sticky bottom-0 bg-background border-t border-border px-5 py-4">
                <Button
                  variant="outline"
                  onClick={() => setShowProductModal(false)}
                  className="w-full rounded-full"
                  size="lg"
                >
                  {t("common.close", { defaultValue: "Close" })}
                </Button>
              </div>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>
      {/* Quick customizer opened from the envelope corner button */}
      {customizerOpen && (
        <Suspense fallback={null}>
          <FabricEditor
            mode="user"
            initialJson={customizerJson}
            onClose={() => setCustomizerOpen(false)}
            onUserSave={handleCustomizerSave}
          />
        </Suspense>
      )}
      {/* Gift Guidance Wizard */}
      <AnimatePresence>
        {showGiftWizard && collection && (
          <GiftGuidanceWizard
            collection={collection}
            onClose={() => setShowGiftWizard(false)}
            onUpdate={(updates) => setCollection({ ...collection, ...updates })}
          />
        )}
      </AnimatePresence>
      {/* Message to group bottom sheet — matches SettingsModal animation */}
      <AnimatePresence>
        {showMessageModal && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            className="fixed inset-0 z-50 bg-black/50 flex items-end sm:items-center justify-center sm:p-4"
            onClick={() => !savingMessage && setShowMessageModal(false)}
          >
            <motion.div
              initial={{ opacity: 0, y: 100 }}
              animate={{ opacity: 1, y: 0 }}
              exit={{ opacity: 0, y: 100 }}
              transition={{ type: "spring", damping: 25, stiffness: 300 }}
              onClick={(e) => e.stopPropagation()}
              className="bg-background rounded-t-2xl sm:rounded-2xl w-full sm:max-w-lg max-h-[90vh] overflow-y-auto shadow-2xl relative"
            >
              {/* Header */}
              <div className="flex items-center py-4 border-b border-border sticky top-0 bg-background rounded-t-2xl z-10 px-4">
                <div className="w-8" />
                <h2 className="text-lg font-bold text-foreground font-serif flex-1 text-center">
                  {t("settings.messageToGroupTitle", { defaultValue: "Message to group" })}
                </h2>
                <button
                  onClick={() => setShowMessageModal(false)}
                  disabled={savingMessage}
                  className="w-8 h-8 rounded-full bg-muted flex items-center justify-center text-muted-foreground hover:text-foreground"
                >
                  <X size={16} />
                </button>
              </div>

              {/* Body */}
              <div className="p-5 space-y-3">
                <p className="text-sm text-muted-foreground">
                  {t("settings.messageToGroupDesc", {
                    defaultValue: "Write a personal message your group will see when they receive the invitation.",
                  })}
                </p>
                <div className="space-y-1.5">
                  <div className="flex justify-end">
                    <span className="text-xs text-muted-foreground tabular-nums">{messageDraft.length}/500</span>
                  </div>
                  <Textarea
                    value={messageDraft}
                    onChange={(e) => setMessageDraft(e.target.value.slice(0, 500))}
                    placeholder={t("settings.messageToGroupPlaceholder", {
                      defaultValue: `Let's surprise ${collection?.recipient_name || "them"} with something special!`,
                      name: collection?.recipient_name || "them",
                    })}
                    className="min-h-[160px] resize-none"
                    rows={6}
                  />
                </div>
              </div>

              {/* Sticky footer — Save + Cancel stacked with vertical spacing */}
              <div className="sticky bottom-0 bg-background border-t border-border px-5 py-4 flex flex-col gap-4">
                <Button
                  onClick={handleSaveMessage}
                  disabled={savingMessage}
                  className="w-full rounded-full bg-primary text-primary-foreground hover:bg-primary/90"
                  size="lg"
                >
                  {savingMessage
                    ? t("common.saving", { defaultValue: "Saving..." })
                    : t("common.save", { defaultValue: "Save" })}
                </Button>
                <Button
                  variant="outline"
                  onClick={() => setShowMessageModal(false)}
                  disabled={savingMessage}
                  className="w-full rounded-full"
                  size="lg"
                >
                  {t("common.cancel", { defaultValue: "Cancel" })}
                </Button>
              </div>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>
      {/* Delete confirmation dialog */}
      <AlertDialog open={showDeleteDialog} onOpenChange={setShowDeleteDialog}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>
              {t("settings.deleteThisCard", { defaultValue: "Delete this card" })}
            </AlertDialogTitle>
            <AlertDialogDescription>
              {t("settings.deleteConfirm", { defaultValue: "Are you sure? This cannot be undone." })}
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel disabled={deleting}>
              {t("common.cancel", { defaultValue: "Cancel" })}
            </AlertDialogCancel>
            <AlertDialogAction
              onClick={handleDelete}
              disabled={deleting}
              className="bg-destructive text-destructive-foreground hover:bg-destructive/90"
            >
              {deleting
                ? t("settings.deleting", { defaultValue: "Deleting..." })
                : t("settings.confirmDelete", { defaultValue: "Yes, delete" })}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
};

// --- Organizer Message + Gift Guidance merged card ---
function OrganizerGuidanceCard({
  collection,
  onAddGiftGuidance,
}: {
  collection: Tables<"collections">;
  onAddGiftGuidance: () => void;
}) {
  const { t } = useTranslation();
  const col = collection as any;
  const hasMessage = !!collection.message_to_group;
  const hasPrefs =
    col.preferences_wants ||
    col.preferences_has ||
    col.preferences_dislikes ||
    col.preferences_style ||
    (col.preferences_registries && col.preferences_registries.length > 0 && col.preferences_registries.some((r: string) => r.trim()));

  const splitToTags = (text: string) =>
    text.split(/[,\n]+/).map(s => s.trim()).filter(Boolean);

  const isValidUrl = (str: string) => {
    try { new URL(str); return true; } catch { return false; }
  };
  const getDomain = (url: string) => {
    try { return new URL(url).hostname.replace("www.", ""); } catch { return url; }
  };

  const recipientName = collection.recipient_name || t("cardView.them");

  return (
    <div className="space-y-3">
      {(hasMessage || hasPrefs) && (
        <div className="bg-card border border-border rounded-2xl overflow-hidden">
          <div className="px-5 pt-5 pb-3 flex items-center justify-between">
            <h3 className="text-xs font-semibold text-foreground uppercase tracking-[0.15em]">
              {t("cardView.giftGuidance")}
            </h3>
            <button
              onClick={onAddGiftGuidance}
              className="text-xs text-primary hover:text-primary/80 font-medium transition-colors"
            >
              {t("cardView.edit")}
            </button>
          </div>
          {hasMessage && (
            <div className="px-5 pb-4">
              <div className="bg-muted/50 rounded-xl p-4">
                <p className="text-[10px] text-muted-foreground uppercase tracking-[0.15em] mb-2">{t("cardView.messageToGroup")}</p>
                <p className="text-sm text-foreground leading-relaxed whitespace-pre-wrap">
                  {collection.message_to_group}
                </p>
              </div>
            </div>
          )}
          {hasPrefs && (
            <div className="border-t border-border divide-y divide-border">
              {col.preferences_wants && (
                <div className="px-5 py-3.5 flex gap-3">
                  <Heart size={15} className="text-primary/60 shrink-0 mt-0.5" />
                  <div className="min-w-0 flex-1">
                    <p className="text-[10px] text-muted-foreground uppercase tracking-[0.15em] mb-1.5">{t("cardView.whatTheyWant", { name: recipientName })}</p>
                    <div className="flex flex-wrap gap-1.5">
                      {splitToTags(col.preferences_wants).map((item: string, i: number) => (
                        <span key={i} className="text-xs text-foreground bg-muted px-2.5 py-1 rounded-full">{item}</span>
                      ))}
                    </div>
                  </div>
                </div>
              )}
              {col.preferences_has && (
                <div className="px-5 py-3.5 flex gap-3">
                  <PackageCheck size={15} className="text-primary/60 shrink-0 mt-0.5" />
                  <div className="min-w-0 flex-1">
                    <p className="text-[10px] text-muted-foreground uppercase tracking-[0.15em] mb-1.5">{t("cardView.alreadyHas")}</p>
                    <div className="flex flex-wrap gap-1.5">
                      {splitToTags(col.preferences_has).map((item: string, i: number) => (
                        <span key={i} className="text-xs text-muted-foreground bg-muted px-2.5 py-1 rounded-full">{item}</span>
                      ))}
                    </div>
                  </div>
                </div>
              )}
              {col.preferences_dislikes && (
                <div className="px-5 py-3.5 flex gap-3">
                  <ThumbsDown size={15} className="text-primary/60 shrink-0 mt-0.5" />
                  <div className="min-w-0 flex-1">
                    <p className="text-[10px] text-muted-foreground uppercase tracking-[0.15em] mb-1.5">{t("cardView.pleaseAvoid")}</p>
                    <div className="flex flex-wrap gap-1.5">
                      {splitToTags(col.preferences_dislikes).map((item: string, i: number) => (
                        <span key={i} className="text-xs text-muted-foreground bg-muted px-2.5 py-1 rounded-full">{item}</span>
                      ))}
                    </div>
                  </div>
                </div>
              )}
              {col.preferences_style && (
                <div className="px-5 py-3.5 flex gap-3">
                  <Palette size={15} className="text-primary/60 shrink-0 mt-0.5" />
                  <div className="min-w-0 flex-1">
                    <p className="text-[10px] text-muted-foreground uppercase tracking-[0.15em] mb-1.5">{t("cardView.styleAndTaste")}</p>
                    <div className="flex flex-wrap gap-1.5">
                      {splitToTags(col.preferences_style).map((item: string, i: number) => (
                        <span key={i} className="text-xs text-foreground bg-muted px-2.5 py-1 rounded-full">{item}</span>
                      ))}
                    </div>
                  </div>
                </div>
              )}
              {col.preferences_registries?.some((r: string) => r.trim() && isValidUrl(r)) && (
                <div className="px-5 py-3.5 flex gap-3">
                  <ExternalLink size={15} className="text-primary/60 shrink-0 mt-0.5" />
                  <div className="min-w-0 flex-1">
                    <p className="text-[10px] text-muted-foreground uppercase tracking-[0.15em] mb-1">{t("cardView.registries")}</p>
                    <div className="flex flex-wrap gap-2">
                      {col.preferences_registries.filter((r: string) => r.trim() && isValidUrl(r)).map((r: string, i: number) => (
                        <a
                          key={i}
                          href={r}
                          target="_blank"
                          rel="noopener noreferrer"
                          className="text-xs text-primary hover:text-primary/80 bg-primary/5 px-3 py-1.5 rounded-full border border-primary/10 transition-colors"
                        >
                          {getDomain(r)}
                        </a>
                      ))}
                    </div>
                  </div>
                </div>
              )}
            </div>
          )}
        </div>
      )}

      {!hasMessage && !hasPrefs && (
        <button
          onClick={onAddGiftGuidance}
          className="w-full bg-card border border-dashed border-border rounded-2xl p-4 flex items-center gap-3 hover:border-primary/40 hover:bg-primary/5 transition-colors group"
        >
          <Heart size={20} className="text-muted-foreground group-hover:text-primary transition-colors" />
          <div className="text-start">
            <p className="text-sm font-medium text-foreground group-hover:text-primary transition-colors">{t("cardView.addGiftGuidance")}</p>
            <p className="text-xs text-muted-foreground">{t("cardView.addGiftGuidanceDesc")}</p>
          </div>
        </button>
      )}
    </div>
  );
}

export default CardManage;
